import React, { useEffect, useState, useContext } from 'react';
import { getInstructorDashboardData } from '../../../services/instructors';
import { showError } from '../../../Utils/helper';
import HomeIcon from '@mui/icons-material/Home';
import Loader from '../../../Common/Loader';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context/AuthContext';
import FlashcardFavorites from './FlashcardFavorites';
import QuizFavorites from './QuizFavorites';
import DashboardHome from './DashboardHome';
import Course from '../Course/Course';
import Connect from '../Connect/Connect';
import './InstructorDashboard.css';
import ReportedIssues from '../ReportedIssues';
import { fileUpload } from '../../../Utils/fileUpload';
import { profileActions } from '../../../../containers/profile/actions/profile'; // adjust path as needed
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const ONBOARDING_URL = 'https://dataroom.dawraty.app/instructors';

function InstructorDashboard() {
    const { t } = useTranslation();
    const { user, refreshUser } = useContext(UserContext);

    const dispatch = useDispatch();
    const profileReducer = useSelector((state) => state.profile);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('home');
    const [selectedCourse, setSelectedCourse] = useState({});
    const [reportedIssues, setReportedIssues] = useState([]);

    const [uploadProgress, setUploadProgress] = useState({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
    const [isUploading, setIsUploading] = useState(false);
    const [cvPath, setCvPath] = useState(null);
    const [cvSubmitRequested, setCvSubmitRequested] = useState(false);

    // used to track CV upload progress
    const onProgress = (progress) => {
        setUploadProgress(progress);
    };

    const handleCVUpload = async (file) => {
        setIsUploading(true);
        setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        try {
            const filePath = await fileUpload(file, `storage/instructor_cv/${user.id}`, onProgress);
            setCvPath('/' + filePath);
        } catch (err) {
            console.error('Upload failed:', err);
        } finally {
            setIsUploading(false);
            setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        }
    };

    const handleSubmitCV = async () => {
        console.log('HIT', { ...user, cv_path: cvPath });
        setCvSubmitRequested(true);
        dispatch(profileActions.updateProfile({ ...user, cv_path: cvPath }, null, null));
    };

    useEffect(() => {
        if (!cvSubmitRequested) return;
        if (profileReducer?.loading) return;
        if (profileReducer?.error) return;

        // Refresh the user in context so the UI updates (cv_path becomes non-null)
        // NOTE(naserAlqimlas): idk whats going on here, there's a lot to do so i offloaded to claude
        if (typeof refreshUser === 'function') {
            refreshUser();
        }
        setCvSubmitRequested(false);
    }, [cvSubmitRequested, profileReducer?.loading, profileReducer?.error, refreshUser]);

    async function toGetDashboardData() {
        setLoading(true);
        const resp = await getInstructorDashboardData();
        if (resp.success) {
            setData(resp.data);
            let setter = [];
            if (resp.data.reported_ai_quiz_issues.length > 0) {
                resp.data.reported_ai_quiz_issues.forEach((issue) => {
                    setter.push(issue);
                });
            }
            if (resp.data.reported_flashcard_issues.length > 0) {
                resp.data.reported_flashcard_issues.forEach((issue) => {
                    setter.push(issue);
                });
            }
            setReportedIssues(setter);
        } else {
            showError(t('alertMessage.wrong'));
        }
        setLoading(false);
    }

    useEffect(() => {
        toGetDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            {loading ? (
                <Loader />
            ) : user?.is_approved ? (
                <div className="instructor-dashboard_container">
                    <div className="instructor-dashboard">
                        <div className="instructor-dashboard_sidebar">
                            <h2>{t('instructorDashboard.myDashboard')}</h2>

                            <div className="instructor-dashboard_sidebar_courses">
                                <h3 className="instructor-dashboard_sidebar_courses_title">
                                    {t('instructorDashboard.myCourses')}
                                </h3>
                                <div id="instructor-dashboard_sidebar_courses_list">
                                    {data?.recent_courses?.map((course, index) => (
                                        <span
                                            onClick={() => {
                                                setSelectedCourse(course);
                                                setActiveTab('course');
                                            }}
                                            key={index}
                                        >
                                            {course.name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <h5
                                onClick={() => setActiveTab('connect')}
                                className="instructor-dashboard_sidebar_connect_title"
                            >
                                {t('instructorDashboard.connect')}
                            </h5>

                            <h5
                                onClick={() => setActiveTab('reported-issues')}
                                className="instructor-dashboard_sidebar_reported_issues_title"
                            >
                                {t('instructorDashboard.reportedIssues')}
                            </h5>
                        </div>

                        <div className="instructor-dashboard_content">
                            <HomeIcon
                                className="instructor-dashboard_content_home_icon"
                                fontSize="large"
                                onClick={() => setActiveTab('home')}
                            />
                            {activeTab === 'home' && (
                                <DashboardHome
                                    fullName={user?.full_name}
                                    totalStudentsCount={data?.total_students_count}
                                    totalCoursesCount={data?.courses_count}
                                    setActiveTab={setActiveTab}
                                />
                            )}

                            {activeTab === 'flashcard-favorites' && <FlashcardFavorites user_id={user?.id} />}

                            {activeTab === 'quiz-favorites' && <QuizFavorites user_id={user?.id} />}

                            {activeTab === 'course' && <Course course={selectedCourse} />}

                            {activeTab === 'reported-issues' && <ReportedIssues reportedIssues={reportedIssues} />}

                            {activeTab === 'connect' && <Connect />}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ height: '60vh', marginTop: '20px' }}>
                    <div className="flex items-center justify-center m-30">
                        <div className="flex flex-col items-center justify-center text-center p-8 w-full m-10">
                            <h2 className="text-2xl font-bold text-gray-800 mb-3">
                                {t('instructorDashboard.thankYouForJoining')}
                            </h2>
                            <h3 className="text-gray-500 text-m leading-relaxed">
                                {user.cv_path === null ? (
                                    t('instructorDashboard.profileUnderReviewUploadCV')
                                ) : (
                                    <>
                                        {t('instructorDashboard.cvUploadedUnderReview')}{' '}
                                        <a
                                            href={ONBOARDING_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#3b82f6', textDecoration: 'underline' }}
                                        >
                                            {ONBOARDING_URL}
                                        </a>{' '}
                                        {t('instructorDashboard.completeOnboarding')}
                                    </>
                                )}
                            </h3>
                            {user.cv_path === null && (
                                <div style={{ marginTop: '16px' }}>
                                    <input
                                        type="file"
                                        id="cv-upload"
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) handleCVUpload(file);
                                        }}
                                    />
                                    {isUploading && uploadProgress.percent === 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    )}
                                    {isUploading && uploadProgress.percent > 0 && (
                                        <div
                                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                                            className="w-full mt-2"
                                        >
                                            <div
                                                className="flex justify-between text-sm text-gray-500 mb-1"
                                                style={{ width: '50%' }}
                                            >
                                                <span>
                                                    {uploadProgress.uploadedMB.toFixed(1)}MB /{' '}
                                                    {uploadProgress.totalMB.toFixed(1)}MB @{' '}
                                                    {uploadProgress.speedMbps.toFixed(1)} MB/s
                                                </span>
                                                <span>{uploadProgress.percent}%</span>
                                            </div>
                                            <div
                                                style={{
                                                    width: '50%',
                                                    backgroundColor: '#e5e7eb',
                                                    borderRadius: '9999px',
                                                    height: '8px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: `${uploadProgress.percent}%`,
                                                        backgroundColor: '#3b82f6',
                                                        height: '8px',
                                                        borderRadius: '9999px',
                                                        transition: 'width 0.3s ease',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {cvPath && !isUploading && (
                                <button
                                    onClick={handleSubmitCV}
                                    style={{
                                        marginTop: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#16a34a',
                                        color: 'white',
                                        fontWeight: '600',
                                        padding: '8px 24px',
                                        borderRadius: '8px',
                                        border: 'none',
                                    }}
                                >
                                    {t('common.submit')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

export default InstructorDashboard;
